import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash';
import { Observable, of, Subscription, tap } from 'rxjs';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { SpecialOfferAPIService } from 'src/app/extensions/services/api/specialOffer-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { CountdownService } from 'src/app/extensions/services/countdown-service.service';
import { SpecialOfferClientFilterModalComponent } from './special-offer-filter-modal/special-offer-filter-modal.component';
import { scrollTop } from 'src/app/extensions/utilities';

@Component({
  selector: 'app-special-offer-list',
  standalone: false,
  templateUrl: './special-offer-list.component.html',
  styleUrl: './special-offer-list.component.scss',
  providers: [ScrollService],
})
export class SpecialOfferListComponent implements OnInit, OnDestroy {
  public windowSizeLgUp = true;
  public currentRoute: string;
  public subscriptions: Subscription[] = [];
  public showBackToTop = false;
  public activeFilterCount = 0;
  specialOffers: app.models.FilteredSpecialOfferClientModel[];
  requestedSpecialOffers: app.models.FilteredSpecialOfferClientModel[];
  pageFilter: app.models.GetSpecialOffersFilterModel;
  public initialFilterState: app.models.GetSpecialOffersFilterModel;
  hasSpecialOffer = false;
  hasRequestedSpecialOffers = false;
  pageIndex = 1;
  countdown$: Observable<string> = of('');
  private maxIndex: number;
  public props: app.models.DynamicPropsDictionary;

  constructor(
    private _router: Router,
    private _windowEventService: WindowEventsService,
    private _scrollService: ScrollService,
    private _specialOfferAPIService: SpecialOfferAPIService,
    private _ngbModal: NgbModal,
    private _countdownService: CountdownService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }
  ngOnInit(): void {
    this.pageFilter = {
      fromDate: null,
      toDate: null,
      dictionaryCampaignTypeIds: [],
      statuses: [],
      dealerExternalEntityIds: [],
    };

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let resizeSubscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(resizeSubscription);
    this.initialFilterState = structuredClone(this.pageFilter);

    this.loadSpecialOffersData();
    this.loadRequestedSpecialOffersData();
    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._specialOfferAPIService.getClientRequestedSpecialOffers(this.pageIndex).subscribe(x => {
          this.requestedSpecialOffers = this.requestedSpecialOffers.concat(x.data.values);
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadSpecialOffersData() {
    this._specialOfferAPIService.getFilteredClientSpecialOffers(this.pageFilter).subscribe(res => {
      this.specialOffers = res;
      this.specialOffers = this.specialOffers.map(offer => ({
        ...offer,
        countdown$: this._countdownService.getCountdown(new Date(offer.expireAt)),
      }));
      this.hasSpecialOffer = this.specialOffers.length != 0;
    });
  }

  loadRequestedSpecialOffersData() {
    this._specialOfferAPIService.getClientRequestedSpecialOffers(this.pageIndex).subscribe(res => {
      this.requestedSpecialOffers = res.data.values;
      this.requestedSpecialOffers = this.requestedSpecialOffers.map(offer => ({
        ...offer,
        countdown$: this._countdownService.getCountdown(new Date(offer.expireAt)),
      }));
      this.props = res.props;
      this.hasRequestedSpecialOffers = this.requestedSpecialOffers.length != 0;
      this.maxIndex = res.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  filterHandler() {
    let instance = this._ngbModal.open(SpecialOfferClientFilterModalComponent, {
      scrollable: true,
    });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.activeFilterCount = result;

        this.initialFilterState = {
          ...this.initialFilterState,
          fromDate: this.pageFilter.fromDate,
          toDate: this.pageFilter.toDate,
          dictionaryCampaignTypeIds: this.pageFilter.dictionaryCampaignTypeIds,
          statuses: this.pageFilter.statuses,
          dealerExternalEntityIds: this.pageFilter.dealerExternalEntityIds,
        };
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }
        this.loadSpecialOffersData();
      }
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.fromDate = null;
    this.pageFilter.toDate = null;
    this.pageFilter.dictionaryCampaignTypeIds = [];
    this.pageFilter.statuses = [];
    this.pageFilter.dealerExternalEntityIds = [];
    this.activeFilterCount = 0;
    this.initialFilterState = {
      ...this.initialFilterState,
      fromDate: this.pageFilter.fromDate,
      toDate: this.pageFilter.toDate,
      dictionaryCampaignTypeIds: this.pageFilter.dictionaryCampaignTypeIds,
      statuses: this.pageFilter.statuses,
      dealerExternalEntityIds: this.pageFilter.dealerExternalEntityIds,
    };
    if (!isEqual(this.pageFilter, this.initialFilterState)) {
      this.pageFilter = structuredClone(this.initialFilterState);
    }
    this.loadSpecialOffersData();
  }

  scrollToTop(): void {
    scrollTop();
  }
}
